import { useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import Breadcrumbs from '@/pages/Blog/components/Breadcrumbs';
import JsonLd, { breadcrumbSchema } from '@/components/base/JsonLd';
import useSeo, { SITE_URL, OG_IMAGE } from '@/hooks/useSeo';

const sections = [
  {
    title: '1. Information We Collect',
    content: 'When you use TNT Coach AI, we may collect the following types of information. Account Information: When you create an account, we collect your email address, name, and authentication credentials. Usage Data: We collect the coaching questions you submit, the coaching styles you select, your saved journal entries, and your in-app activity patterns. Device Information: We collect device type, operating system version, app version, and anonymous device identifiers. Payment Information: For paid subscriptions, payment processing is handled by the app store (Apple App Store or Google Play Store). We do not collect or store your full credit card details.',
  },
  {
    title: '2. How We Use Your Information',
    content: 'We use your information to provide and improve the Service. Specifically, we use it to generate coaching responses tailored to your input, maintain and improve the AI coaching engine, process your subscription and manage your account, send you service-related communications and product updates, analyze usage patterns to improve the product experience, and detect, prevent, and address technical issues or abuse. We do not sell your personal data to third parties.',
  },
  {
    title: '3. AI Data Processing',
    content: 'To generate coaching responses, your submitted questions and selected coaching style are processed by our AI models. This processing occurs on secure servers. Your coaching inputs may be used in an anonymized and aggregated form to improve the quality of our AI models. We implement measures to prevent your personally identifiable information from being included in training datasets.',
  },
  {
    title: '4. Data Storage and Security',
    content: 'We implement industry-standard security measures to protect your data, including encryption in transit and at rest, secure server infrastructure, and regular security reviews. While we take reasonable precautions, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security of your data.',
  },
  {
    title: '5. Data Retention',
    content: 'We retain your coaching history, journal entries, and account information for as long as your account remains active. If you delete your account, we will permanently delete your personal data within 30 days, except for data we are required to retain for legal or legitimate business purposes. Anonymized and aggregated data may be retained indefinitely for analytical purposes.',
  },
  {
    title: '6. Your Rights',
    content: 'Depending on your location, you may have the following rights regarding your personal data. Right to Access: You can request a copy of the personal data we hold about you. Right to Rectification: You can request that we correct inaccurate or incomplete data. Right to Erasure: You can request that we delete your personal data, subject to legal retention requirements. Right to Data Portability: You can request your data in a structured, machine-readable format. Right to Object: You can object to certain processing of your data. To exercise any of these rights, contact us at privacy@tntcoach.ai.',
  },
  {
    title: '7. Cookies and Tracking Technologies',
    content: 'Our mobile application may use session identifiers and similar technologies necessary for authentication, session management, and basic analytics. We use these to remember your preferences, keep you signed in, and understand how you interact with the app. You can control certain tracking preferences through your device settings.',
  },
  {
    title: '8. Third-Party Services',
    content: 'Our Service integrates with third-party services that have their own privacy practices. Payment Processing: Managed by Apple (App Store) and Google (Play Store). Please review their respective privacy policies. Analytics: We may use third-party analytics services to understand app usage. These services collect anonymous usage data and are bound by their own privacy policies. We do not share your coaching content with analytics providers.',
  },
  {
    title: '9. Children\'s Privacy',
    content: 'TNT Coach AI is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal data, we will take steps to delete such information. If you believe a child under 13 has provided us with personal data, please contact us immediately.',
  },
  {
    title: '10. International Data Transfers',
    content: 'Your information may be transferred to and processed in countries other than your country of residence. We ensure that appropriate safeguards are in place to protect your data in accordance with applicable data protection laws. By using the Service, you consent to such transfers.',
  },
  {
    title: '11. Changes to This Privacy Policy',
    content: 'We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy within the app and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically to stay informed about how we protect your data.',
  },
  {
    title: '12. California Privacy Rights',
    content: 'If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA). These include the right to know what personal information we collect, the right to request deletion of your personal information, the right to opt out of the sale of your personal information (though we do not sell personal data), and the right to non-discrimination for exercising your privacy rights. To exercise your CCPA rights, contact us at privacy@tntcoach.ai.',
  },
  {
    title: '13. Contact',
    content: 'If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at privacy@tntcoach.ai.',
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSeo({
    title: 'Privacy Policy | TNT Coach AI',
    description: 'Learn how TNT Coach AI collects, uses, and protects your data, including AI processing, storage, retention, your privacy rights, and how to contact us.',
    canonical: `${SITE_URL}/privacy`,
    url: `${SITE_URL}/privacy`,
    image: OG_IMAGE,
  });

  return (
    <div className="min-h-screen bg-midnight">
      <JsonLd
        data={breadcrumbSchema([
          { name: 'Home', url: `${SITE_URL}/` },
          { name: 'Privacy Policy', url: `${SITE_URL}/privacy` },
        ])}
      />
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-32 pb-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Privacy Policy' }]} />
              <p className="text-champagne-gold text-sm font-medium tracking-wider uppercase mb-3">Legal</p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-silver-grey mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Privacy Policy
              </h1>
              <p className="text-silver-grey/60 text-sm">
                Last updated: June 29, 2026
              </p>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="border-t border-royal-navy/30" />
        </div>

        {/* Privacy Content */}
        <section className="py-12 md:py-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto space-y-10">
              {sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-lg md:text-xl font-semibold text-champagne-gold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {section.title}
                  </h2>
                  <p className="text-silver-grey/80 leading-relaxed text-sm md:text-base">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Back link */}
        <section className="pb-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <a
                href="/"
                className="inline-flex items-center gap-2 text-champagne-gold hover:text-champagne-gold/80 transition-colors duration-300 text-sm"
              >
                <i className="ri-arrow-left-line" />
                Back to Home
              </a>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}